import React, { useEffect, useState, useRef } from 'react';
import PropTypes from 'prop-types';
import ContentLoaderPlaceholder from '@welldev-group/rcl-content-loader-placeholder';
import { useQueryParams, withDefault, StringParam } from 'use-query-params';
import Card from '@welldev-group/rcl-card';
import Icon from '@welldev-group/rcl-icon';
import {
  BreadcrumbItem,
  Breadcrumbs,
  I18nText,
  SortingPill,
  RangeFilter,
  PillTabs,
  DropDownFilter,
} from '@welldev-group/react-comp-lib';
import clipboard from 'affiliateIcons/clipboard-list.svg';
import routes from '../../../shared/routes';
import httpClient from '../../../shared/libraries/httpClient';
import PaginationWithTranslation from '../../../shared/components/Pagination/PaginationWithTranslation';

import TransactionTable from './TransactionTable';
import InvoicesTable from './InvoicesTable';
import withQueryParamsProvider from '../../../shared/components/withQueryParamsProvider';

const TAB_MAPPER = {
  transactions: 0,
  invoices: 1,
};

/* We use this constant to check that we will modify the maxThreshold state
during the initial render only if initialMaxAmount > maxThreshold */
const MAX_THRESHOLD_AMOUNT = 999_999_999_999_999;

const TransactionList = ({ urls, affiliateId }) => {
  const [transactions, setTransactions] = useState([]);
  const [invoices, setInvoices] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPage] = useState(null);
  const [isLoadingTransactions, setIsLoadingTransactions] = useState(true);
  const [isLoadingInvoices, setIsLoadingInvoices] = useState(true);
  const [invoiceCurrentPage, setInvoiceCurrentPage] = useState(1);
  const [invoiceTotalPages, setInvoiceTotalPage] = useState(null);

  const [query] = useQueryParams({
    tab: withDefault(StringParam, 'transactions'),
  });
  const { tab } = query;
  const [selectedTabKey, setSelectedTabKey] = useState(
    TAB_MAPPER[tab] || TAB_MAPPER.transactions,
  );

  const [column, setColumn] = useState(null);
  const [direction, setDirection] = useState(null);
  const [minThreshold, setMinThreshold] = useState(0);
  const [minAmount, setMinAmount] = useState(null);
  const [maxAmount, setMaxAmount] = useState(null);
  const [maxThreshold, setMaxThreshold] = useState(MAX_THRESHOLD_AMOUNT);
  const [transactionType, setTransactionType] = useState(null);
  const isInitialRenderRef = useRef(true);

  const setSelectedTabKeyOnClick = tabindex => {
    setSelectedTabKey(tabindex);
  };

  const setInitialMinMaxThresholdAmount = range => {
    const initialMinAmount = range.minAmount || minThreshold;
    const initialMaxAmount = range.maxAmount || maxThreshold;

    if (!minAmount || initialMinAmount < minThreshold) {
      setMinThreshold(initialMinAmount);
    }

    if (!maxAmount || initialMaxAmount > maxThreshold) {
      setMaxThreshold(initialMaxAmount);
    }
  };

  const fetchTransactions = async page => {
    const { data } = await httpClient.get(
      routes.billing.transactions.list({
        page,
        affiliate_id: affiliateId,
        column,
        direction,
        min_amount: minAmount,
        max_amount: maxAmount,
        transaction_type: transactionType,
      }),
    );
    setTransactions(data.transactions);
    setCurrentPage(data.pagination.currentPage);
    setTotalPage(data.pagination.totalPages);
    setIsLoadingTransactions(false);

    if (isInitialRenderRef.current) {
      isInitialRenderRef.current = false;
      setInitialMinMaxThresholdAmount(data.amountRange);
    }
  };

  const fetchOpenInvoices = async page => {
    const { data } = await httpClient.get(
      routes.billing.invoices.list({
        state: 'open',
        page,
        affiliate_id: affiliateId,
        column,
        direction,
        min_amount: minAmount,
        max_amount: maxAmount,
      }),
    );

    setInvoices(data.invoices);
    setInvoiceCurrentPage(data.pagination.currentPage);
    setInvoiceTotalPage(data.pagination.totalPages);
    setIsLoadingInvoices(false);

    if (isInitialRenderRef.current) {
      isInitialRenderRef.current = false;
      setInitialMinMaxThresholdAmount(data.amountRange);
    }
  };

  useEffect(() => {
    if (selectedTabKey === 0) {
      setIsLoadingTransactions(true);
      fetchTransactions();
    } else {
      setIsLoadingInvoices(true);
      fetchOpenInvoices();
    }
  }, [selectedTabKey]);

  useEffect(() => {
    if (!isInitialRenderRef.current) {
      if (selectedTabKey === 0) {
        fetchTransactions();
      } else {
        fetchOpenInvoices();
      }
    }
  }, [minAmount, maxAmount, column, direction, transactionType]);

  const toggleDirection = () => {
    if (direction === null) {
      setDirection('up');
    } else if (direction === 'up') {
      setDirection('down');
    } else {
      setDirection(null);
    }
  };

  const setSortOrder = chosenColumn => {
    if (column === chosenColumn) {
      toggleDirection();
    } else {
      setColumn(chosenColumn);
      setDirection('up');
    }
  };

  const handleMinMaxAmount = ({ min, max }) => {
    setMinAmount(min);
    setMaxAmount(max);
  };

  const transactionTableContent = () => {
    return transactions.length ? (
      <>
        <TransactionTable transactions={transactions} />
        <PaginationWithTranslation
          currentPage={currentPage}
          totalPages={totalPages}
          onPaginationClick={value => {
            setCurrentPage(value);
            fetchTransactions(value);
          }}
        />
      </>
    ) : (
      <Card version="v2" className="billing-transaction-list__empty-card">
        <div className="billing-transaction-list__empty-card-icon">
          <img src={clipboard} alt="clip-board" />
        </div>
        <div className="billing-transaction-list__empty-card-text">
          <I18nText id="billing.transactions.list.no_pending_invoices" />
        </div>
      </Card>
    );
  };

  const invoiceTableContent = () => {
    return invoices.length ? (
      <>
        <InvoicesTable invoices={invoices} />
        <PaginationWithTranslation
          currentPage={invoiceCurrentPage}
          totalPages={invoiceTotalPages}
          onPaginationClick={invoiceValue => {
            setInvoiceCurrentPage(invoiceValue);
            fetchOpenInvoices(invoiceValue);
          }}
        />
      </>
    ) : (
      <Card version="v2" className="billing-transaction-list__empty-card">
        <div className="billing-transaction-list__empty-card-icon">
          <img src={clipboard} alt="clip-board" />
        </div>
        <div className="billing-transaction-list__empty-card-text">
          <I18nText id="billing.transactions.list.no_pending_invoices" />
        </div>
      </Card>
    );
  };

  const invoiceAndTransactionData = [
    {
      key: 0,
      title: <I18nText id="billing.transactions.list.transactions_title" />,
      content: (
        <>
          {isLoadingTransactions ? (
            <ContentLoaderPlaceholder numberOfLines={20} />
          ) : (
            transactionTableContent()
          )}
        </>
      ),
    },
    {
      key: 1,
      title: <I18nText id="billing.transactions.list.open_invoices_header" />,
      content: (
        <>
          {isLoadingInvoices ? (
            <ContentLoaderPlaceholder numberOfLines={20} />
          ) : (
            invoiceTableContent()
          )}
        </>
      ),
    },
  ];

  const getItems = () => {
    return invoiceAndTransactionData.map(({ title, content }, index) => ({
      key: index,
      title,
      getContent: () => content,
    }));
  };

  const typeOptions = [
    {
      label: <I18nText id="billing.transactions.type.top_up" />,
      value: 'top_up',
    },
    {
      label: <I18nText id="billing.transactions.type.subscription" />,
      value: 'subscription_charge',
    },
    { label: <I18nText id="billing.transactions.type.show_all" />, value: '' },
  ];

  const onTypeFilterChange = value => {
    if (value === '') {
      setTransactionType(null);
    } else {
      setTransactionType(value);
    }
  };

  const getAmountValue = (value, thresholdValue) => {
    if (value !== null) {
      return value;
    }

    return thresholdValue;
  };

  return (
    <div className="container-full">
      <div className="billing__header">
        <div className="billing__header-title">
          <I18nText id="billing.transactions.title" />
        </div>
        <div className="billing__breadcrumbs">
          <Breadcrumbs>
            <BreadcrumbItem linkUrl={urls.billing}>
              <I18nText id="billing.header" />
            </BreadcrumbItem>
            <BreadcrumbItem linkUrl={urls.transactions}>
              <I18nText id="billing.transactions.title" />
            </BreadcrumbItem>
          </Breadcrumbs>
        </div>
      </div>
      <div className="billing__sort-and-filter-section">
        <SortingPill
          size="small"
          sortDirection={column === 'title' ? direction : null}
          parameterName={
            <I18nText
              id="billing.transactions.list.table_header.title"
              returnStringOnly={true}
            />
          }
          onClick={() => setSortOrder('title')}
        />
        <SortingPill
          size="small"
          sortDirection={column === 'amount' ? direction : null}
          parameterName={
            <I18nText
              id="billing.transactions.list.table_header.amount"
              returnStringOnly={true}
            />
          }
          onClick={() => setSortOrder('amount')}
        />
        <SortingPill
          size="small"
          sortDirection={column === 'created_at' ? direction : null}
          parameterName={
            <I18nText
              id="billing.transactions.list.table_header.date"
              returnStringOnly={true}
            />
          }
          onClick={() => setSortOrder('created_at')}
        />
        <SortingPill
          size="small"
          sortDirection={column === 'balance' ? direction : null}
          parameterName={
            <I18nText
              id="billing.transactions.list.table_header.balance"
              returnStringOnly={true}
            />
          }
          onClick={() => setSortOrder('balance')}
        />
        <div className="billing__sort-and-filter-section-type-filter">
          <DropDownFilter
            selected={transactionType}
            label={
              <div className="billing__sort-and-filter-section-type-filter__label-container">
                <Icon name="orders" size="small" />
                <I18nText id="billing.transactions.type.label" />
              </div>
            }
            onChange={onTypeFilterChange}
            options={typeOptions}
            popoverMenuPosition="bottom-right"
            isSearchable={false}
          />
        </div>
        <RangeFilter
          label={
            <I18nText id="billing.transactions.list.table_header.amount" />
          }
          title={
            <I18nText id="billing.transactions.list.table_header.amount" />
          }
          minThreshold={minThreshold}
          maxThreshold={maxThreshold}
          min={getAmountValue(minAmount, minThreshold)}
          max={getAmountValue(maxAmount, maxThreshold)}
          onChange={handleMinMaxAmount}
        />
      </div>
      <div className="grid">
        <div className="col-12">
          <PillTabs
            items={getItems()}
            selectedTabKey={selectedTabKey}
            onChange={setSelectedTabKeyOnClick}
          />
        </div>
      </div>
    </div>
  );
};

TransactionList.propTypes = {
  affiliateId: PropTypes.number.isRequired,
  urls: PropTypes.shape({
    billing: PropTypes.string.isRequired,
    transactions: PropTypes.string.isRequired,
  }).isRequired,
};

export default withQueryParamsProvider(TransactionList);
